'use client';

import { useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { DeepDiveHeader, type DeepDiveChip } from './deep-dive-header';

/** Filter params a chip / "clear all" can remove — everything except the
 *  section/sort/query context that defines the deep-dive itself. */
const FILTER_PARAMS = ['dietary', 'cuisine', 'category', 'variant', 'maxCookTime', 'cooked', 'cookbook'];

interface DeepDiveHeaderNavProps {
  title: string;
  backLabel: string;
  count?: number;
  countUnitLabel?: string;
  sortLabel?: string;
  sortedLabel?: string;
  dir?: 'asc' | 'desc';
  chips?: DeepDiveChip[];
  clearAllLabel?: string;
}

/**
 * Client wrapper that drives the RSC-rendered {@link DeepDiveHeader} via the
 * URL: back → the dashboard, clear-chip → drop that one filter param,
 * clear-all → drop every filter but keep the section/sort context.
 */
export function DeepDiveHeaderNav({
  title,
  backLabel,
  count,
  countUnitLabel,
  sortLabel,
  sortedLabel,
  dir,
  chips,
  clearAllLabel,
}: DeepDiveHeaderNavProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const push = useCallback(
    (params: URLSearchParams) => {
      const qs = params.toString();
      router.push(qs ? `/recipes?${qs}` : '/recipes', { scroll: false });
    },
    [router],
  );

  const onBack = useCallback(() => {
    router.push('/recipes', { scroll: false });
  }, [router]);

  const onClearChip = useCallback(
    (key: string) => {
      const params = new URLSearchParams(searchParams.toString());
      const dropFromCsv = (param: string, value: string) => {
        const cur = params.get(param);
        if (!cur) return;
        const next = cur
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s && s !== value);
        if (next.length) params.set(param, next.join(','));
        else params.delete(param);
      };
      if (key.startsWith('dietary:')) dropFromCsv('dietary', key.slice('dietary:'.length));
      else if (key.startsWith('cuisine:')) dropFromCsv('cuisine', key.slice('cuisine:'.length));
      else if (key.startsWith('category:')) dropFromCsv('category', key.slice('category:'.length));
      else params.delete(key); // variant | maxCookTime | cooked | cookbook
      push(params);
    },
    [searchParams, push],
  );

  const onClearAll = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    for (const p of FILTER_PARAMS) params.delete(p);
    push(params);
  }, [searchParams, push]);

  return (
    <DeepDiveHeader
      title={title}
      backLabel={backLabel}
      count={count}
      countUnitLabel={countUnitLabel}
      sortLabel={sortLabel}
      sortedLabel={sortedLabel}
      dir={dir}
      chips={chips}
      clearAllLabel={clearAllLabel}
      onBack={onBack}
      onClearChip={onClearChip}
      onClearAll={onClearAll}
    />
  );
}
